import React, { useCallback, useEffect, useState, useRef } from 'react' // Add useRef
import { debounce, Box, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { Search } from 'lucide-react'
import { CampaignViewHeaderProps } from './CampaignViewHeaderTypes'
import { usePageNavigation } from 'hooks/useNavigation'
import { useNavigate } from 'react-router-dom'


export default function CampaignViewHeader({
  setSearchString,
  searchString,
  filterData,
}: CampaignViewHeaderProps): JSX.Element {
  const { navigateToNewCampaign } = usePageNavigation()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const open = Boolean(anchorEl)

  const debouncedFilterData = useCallback(
    debounce((text: string) => {
      filterData({ filter: { search: text } }).catch(() => {})
    }, 500),
    []
  )

  useEffect(() => {
    debouncedFilterData(searchString)
  }, [searchString])

  const handleClick = () => {
    setAnchorEl(buttonRef.current)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleAICampaign = () => {
    handleClose()
    navigateToNewCampaign()
  }

  const handleCompetitorCampaign = () => {
    handleClose()
    navigate('/campaigns/new/competitor')
  }

  return (
    <Box sx={{ marginBottom: '24px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap',
        }}
      >
        {/* Search Input */}
        <Box sx={{ flex: '1 1 300px', position: 'relative' }}>
          <Search
            style={{
              position: 'absolute',
              left: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              width: '20px',
              height: '20px',
            }}
          />
          <input
            type="text"
            value={searchString}
            onChange={(e) => setSearchString(e.target.value)}
            placeholder="Search campaigns..."
            style={{
              width: '100%',
              paddingLeft: '48px',
              paddingRight: '16px',
              paddingTop: '12px',
              paddingBottom: '12px',
              background: 'rgba(30, 41, 59, 0.5)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(168, 85, 247, 0.2)',
              borderRadius: '12px',
              color: 'white',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'rgba(168, 85, 247, 0.5)'
              e.target.style.boxShadow = '0 0 0 3px rgba(168, 85, 247, 0.1)'
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(168, 85, 247, 0.2)'
              e.target.style.boxShadow = 'none'
            }}
          />
        </Box>

        {/* New Campaign Button */}
        <Box>
          <button
            ref={buttonRef}
            onClick={handleClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
              border: 'none',
              borderRadius: '12px',
              color: 'white',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 4px 12px rgba(147, 51, 234, 0.3)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)'
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(147, 51, 234, 0.4)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(147, 51, 234, 0.3)'
            }}
          >
            New Campaign
            <KeyboardArrowDownIcon sx={{ fontSize: '20px' }} />
          </button>

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                backgroundColor: 'rgba(30, 41, 59, 0.95)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(168, 85, 247, 0.2)',
                borderRadius: '12px',
                minWidth: '280px',
                marginTop: '8px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
              },
            }}
          >
            <MenuItem
              onClick={handleAICampaign}
              sx={{
                padding: '14px 16px',
                borderRadius: '8px',
                margin: '4px',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: 'rgba(147, 51, 234, 0.15)',
                  transform: 'translateX(4px)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 'unset', // prevents default 56px spacing
                  marginRight: '12px', // adjust spacing here
                }}
                >
                <Box
                  sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.2) 0%, rgba(236, 72, 153, 0.2) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <SmartToyIcon sx={{ color: '#a855f7', fontSize: '20px' }} />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary="AI Campaign Assistant"
                secondary="Create with conversational AI"
                primaryTypographyProps={{
                  sx: { color: '#ffffff', fontWeight: 600, fontSize: '14px' },
                }}
                secondaryTypographyProps={{
                  sx: { color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px' },
                }}
              />
            </MenuItem>

            <MenuItem
              onClick={handleCompetitorCampaign}
              sx={{
                padding: '14px 16px',
                borderRadius: '8px',
                margin: '4px',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: 'rgba(249, 115, 22, 0.15)',
                  transform: 'translateX(4px)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 'unset', // prevents default 56px spacing
                  marginRight: '12px', // adjust spacing here
                }}
                >
                <Box
                  sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.2) 0%, rgba(234, 179, 8, 0.2) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <TrendingUpIcon sx={{ color: '#f97316', fontSize: '20px' }} />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary="Competitor Analysis"
                secondary="Track and analyze competitors"
                primaryTypographyProps={{
                  sx: { color: '#ffffff', fontWeight: 600, fontSize: '14px' },
                }}
                secondaryTypographyProps={{
                  sx: { color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px' },
                }}
              />
            </MenuItem>
          </Menu>
        </Box>
      </Box>
    </Box>
  )
}
